import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  findBriaPreset,
  findGptImagePreset,
  downloadJobOutput,
  type CleanupPolicy,
  type JobStatus,
  type PackshotJob,
  type PackshotJobCard,
  type PackshotPresets,
  type RetryJobInput,
} from '@/lib/packshots';
import { cn } from '@/lib/utils';
import {
  jobDisplayName,
  isServiceBusyError,
  statusHeadline,
  userFacingError,
} from './jobStatus';
import ModelLogs from './ModelLogs';

type Props = {
  job: PackshotJob | null;
  card: PackshotJobCard | null;
  loading: boolean;
  loadError: string | null;
};

type ActionProps = {
  job: PackshotJob | null;
  card: PackshotJobCard | null;
  canWrite: boolean;
  viewerId: string | null;
  presets: PackshotPresets | null;
  pending: boolean;
  onRetry: (input: RetryJobInput) => void;
  onApprove: () => void;
  onDelete: () => void;
};

export function JobActions({
  job,
  card,
  canWrite,
  viewerId,
  presets,
  pending,
  onRetry,
  onApprove,
  onDelete,
}: ActionProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const current = job ?? card;
  const name = current ? jobDisplayName(current) : 'Image';
  const gpt = presets ? findGptImagePreset(presets.cleanup) : undefined;
  const bria = presets ? findBriaPreset(presets.cutout) : undefined;
  const showOutput = Boolean(current?.files.output);
  const showFailure = current?.status === 'failed' && !isServiceBusyError(current.error?.message);
  const canDelete = Boolean(current && viewerId && current.userId === viewerId);
  const deleteHint = current?.ownerEmail
    ? `Only ${current.ownerEmail} can delete this`
    : 'Only the owner can delete this';

  if (!current) return null;

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
      {canWrite && current.status === 'needs_review' && (
        <Button type="button" disabled={pending} onClick={onApprove}>Looks good</Button>
      )}
      {showOutput && (current.status === 'completed' || current.status === 'needs_review') && (
        <Button
          type="button"
          variant={current.status === 'completed' ? 'default' : 'outline'}
          disabled={saving}
          onClick={() => {
            setSaving(true);
            downloadJobOutput(current.id, name, current.updatedAt)
              .catch((err: unknown) => {
                toast.error(err instanceof Error && err.message ? err.message : 'Could not download this image');
              })
              .finally(() => setSaving(false));
          }}
        >
          Download
        </Button>
      )}
      {canWrite && (showFailure || current.status === 'needs_review') && (
        <RetryMenu
          primary={current.status === 'failed'}
          disabled={pending}
          gptKey={gpt?.key}
          briaKey={bria?.key}
          onRetry={onRetry}
        />
      )}
      {canWrite && (viewerId === null || canDelete) && (
        <Button type="button" variant="destructive" disabled={pending || viewerId === null} onClick={() => setConfirmDelete(true)}>
          Delete
        </Button>
      )}
      {canWrite && viewerId !== null && !canDelete && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex" tabIndex={0}>
              <Button type="button" variant="outline" disabled className="border-transparent bg-muted text-muted-foreground disabled:opacity-100">
                Delete
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>{deleteHint}</TooltipContent>
        </Tooltip>
      )}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this image?</DialogTitle>
            <DialogDescription>This removes the image and its files.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={pending} onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button
              type="button"
              variant="destructive"
              disabled={pending}
              onClick={() => {
                setConfirmDelete(false);
                onDelete();
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function JobStatusBanner({
  job,
  card,
}: {
  job: PackshotJob | null;
  card: PackshotJobCard | null;
}) {
  const current = job ?? card;
  const issues = job?.qa?.issues.filter((issue) => issue.trim().length > 0) ?? [];
  const issueText = issues.join('. ');
  const serviceBusy = isServiceBusyError(current?.error?.message);
  const showFailure = current?.status === 'failed' && !serviceBusy;
  if (!current || serviceBusy || current.status === 'completed') return null;

  return (
    <div className={cn('rounded-lg border px-4 py-3', statusPanelClass(current.status))} role="status">
      <p className="flex items-center gap-2 text-base font-semibold">
        {current.status === 'needs_review' && <AlertTriangle className="size-5 shrink-0 text-amber-400" strokeWidth={2.5} aria-hidden="true" />}
        {statusHeadline(current.status)}
      </p>
      {current.status === 'needs_review' && issueText && (
        <p className="mt-1 text-sm leading-relaxed">{issueText}</p>
      )}
      {showFailure && current.error?.message && (
        <p className="mt-1 text-sm leading-relaxed">{userFacingError(current.error.message)}</p>
      )}
    </div>
  );
}

export default function JobDetail({
  job,
  card,
  loading,
  loadError,
}: Props) {
  const current = job ?? card;
  const name = current ? jobDisplayName(current) : 'Image';

  return (
    <section className="space-y-5" aria-label={name}>
      {loading && !current && <Skeleton className="h-24 w-full" />}
      {loadError && !current && <p className="text-sm text-destructive">{loadError}</p>}

      {job && <ModelLogs runs={job.providerRuns} />}
    </section>
  );
}

function statusPanelClass(status: JobStatus): string {
  switch (status) {
    case 'needs_review':
      return 'border-amber-400 bg-card text-foreground';
    case 'failed':
      return 'border-destructive/40 bg-destructive/10 text-destructive';
    case 'completed':
      return 'border-emerald-600/30 bg-emerald-50 text-emerald-950 dark:border-emerald-400/30 dark:bg-emerald-950 dark:text-emerald-50';
    case 'queued':
    case 'processing':
    case 'sourcing':
      return 'border-sky-600/30 bg-sky-50 text-sky-950 dark:border-sky-400/30 dark:bg-sky-950 dark:text-sky-50';
    case 'awaiting_pick':
    case 'no_source_found':
      return 'border-border bg-muted text-foreground';
    default: {
      const unreachable: never = status;
      return unreachable;
    }
  }
}

function RetryMenu({
  primary,
  disabled,
  gptKey,
  briaKey,
  onRetry,
}: {
  primary: boolean;
  disabled: boolean;
  gptKey: string | undefined;
  briaKey: string | undefined;
  onRetry: (input: RetryJobInput) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant={primary ? 'default' : 'outline'} disabled={disabled}>
          Retry ▾
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem onSelect={() => onRetry({})}>Retry</DropdownMenuItem>
        {gptKey && (
          <DropdownMenuItem onSelect={() => onRetry({ cleanupPreset: gptKey })}>Retry with GPT Image</DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => onRetry({ cleanupPolicy: 'never' satisfies CleanupPolicy })}>
          Retry without cleanup
        </DropdownMenuItem>
        {briaKey && (
          <DropdownMenuItem onSelect={() => onRetry({ cutoutPreset: briaKey })}>Retry with Bria cutout</DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
